import { Injectable } from '@angular/core';
import { SxSnackbarService } from './sx-snackbar.service';
import { Subject, throwError } from 'rxjs';
import { ApplicationConfiguration, BannerMessage, SXError } from '@model';
import * as fromReducers from '@appStore/account/reducers';
import * as fromActions from '@appStore/account/actions';
import * as fromConfiguration from '@appStore/configuration';
import * as fromInformationStore from '@core/store';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { SxDialogService } from './sx-dialog.service';
// tslint:disable-next-line:max-line-length
import { RegisterCustomerDialogComponent } from 'app/secure/register/components/register-customer-dialog/register-customer-dialog.component';
import { SessionExpiredDialogComponent } from '@core/components/session-expired/session-expired-dialog-component';
import { SxApplicationInsightsService } from './sx-application-insights.service';
import { filter, takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SxErrorService {
  public hideableErrorCodes: string[];
  public hideErrorCodes = false;
  public bannerMessages: BannerMessage[];
  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private sxSnackbarService: SxSnackbarService,
    private store: Store<fromReducers.AccountState>,
    private translate: TranslateService,
    private sxDialogService: SxDialogService,
    private aiService: SxApplicationInsightsService,
    private configStore: Store<fromConfiguration.State>,
    private informationStore: Store<fromInformationStore.InformationState>
  ) {
      let appConfig$ = this.configStore.select(fromConfiguration.getApiConfiguration);
      appConfig$
      .pipe(
        filter(x => !!x),
        takeUntil(this.destroy$)
      )
      .subscribe(apiConfig => {
        this.hideableErrorCodes = apiConfig?.hideableErrorCodes.split(';');
        this.aiService.initService(apiConfig?.applicationInsightsKey);
      });

      let messages$ = this.informationStore.select(fromInformationStore.getBannerMessages);
      messages$
      .pipe(
        filter(x => !!x),
        takeUntil(this.destroy$)
      )
      .subscribe(messages => {
        this.hideErrorCodes = messages?.some(x => x.severity === 4); // Any active BannerMessage with severity 4
      });
  }

  handleError(error: any) {
    let title = 'Fel!';
    let message = 'Ett fel har uppstått!';

    if (error.error) {
      message = this.translate.instant(error.error.messageKey);
      if (message === error.error.messageKey) {
        message = error.error.message;
      }

      // Validation errors
      if (error.error.errorCode === 5) {
        title = 'Valideringsfel:';
        this.sxSnackbarService.openValidationErrorSnackBar(message, title, error.error.validationErrors);
      } else if (error.error.errorCode === 6) {
        this.sxDialogService.openDialog(RegisterCustomerDialogComponent, error.error);
      } else if (error.error.errorCode === 7) {
      } else if (this.hideErrorCodes && this.hideableErrorCodes.includes(error.error.errorCode.toString())) {
        return; // Don't show Error
      } else {
        this.sxSnackbarService.openErrorSnackBar(message, title, 3000);
      }

      this.store.dispatch(
        new fromActions.ErrorAdd({
          statusCode: error.error.errorCode,
          statusMessage: message,
          errorMessage: '',
          stackTrace: error.error.StackTraceString,
          timestamp: new Date()
        } as SXError)
      );
      if (error.error.errorCode !== 5) {
        this.logError(error);
      }
    } else if (error.status) {
      switch (error.status) {
        case 404:
          this.sxSnackbarService.openErrorSnackBar(
            'Anropet som gjordes misslyckades pga att den inte kan nås (404)',
            'Anrop misslyckades',
            3000
          );
          break;
        case 403:
          this.sxDialogService.openDialog(SessionExpiredDialogComponent, null);
          break;
        case 500:
          this.sxSnackbarService.openErrorSnackBar(
            'Ett okänt fel har inträffat. Felet har registrerats.',
            'Ett okänt fel',
            3000
          );
          break;
      }
    } else {
      this.store.dispatch(
        new fromActions.ErrorAdd({
          statusCode: error.status,
          statusMessage: error.statusText,
          errorMessage: error.Message,
          stackTrace: error.error ? error.error.StackTraceString : 'Unknown',
          timestamp: new Date()
        } as SXError)
      );
    }

    return throwError(error);
  }

  public logError(error: Error, properties?: { [key: string]: string }, measurements?: { [key: string]: number }) {
    if (!properties) {
      properties = {};
    }
    // Logg to Application Insight
    this.aiService.logException(error, null, properties, measurements);
  }
}
